import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  FormLabel,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
  Box,
  Stack,
  CircularProgress,
  Autocomplete,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import { createCfApprovalV2 } from "./api";
import {
  REGION_OPTIONS,
  CIRCLE_OPTIONS,
  FORM_LABELS,
  FORM_PLACEHOLDERS,
  VALIDATION_ERRORS,
} from "./config";
import {
  invokeApi,
  HTTP_METHODS,
} from "../../utils/http-service";
import { CF_APPROVAL_HOSTNAME } from "../../../common-utilities/utils/end-points";
import { REST_URLS } from "../../../common-utilities/utils/end-points/cf-approval";
import SiFileUpload from "../../core/file-uploader";
import { ATTACHMENT_FIELDS, loadCfFormCityOptions } from "../../utils/constants";

const CreateCfV2Form = ({ onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    siteName: "",
    address: {
      region: "",
      circle: "",
      city: "",
    },
    partOfAOP: false,
    aopStoreId: "",
    relocated: false,
    allDocumentsUploaded: false,
    attachments: {
      unitEconomics: null,
      siteRecommendationDeck: null,
      addressExcelFile: null,
      addressWordFile: null,
      layout: null,
      capexSheet: null,
      geoiqReport: null,
    },
  });
  const [errors, setErrors] = useState({});
  const [aopSites, setAopSites] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);

  useEffect(() => {
    invokeApi(HTTP_METHODS.GET, `${CF_APPROVAL_HOSTNAME}${REST_URLS.AOP_SITES}`)
      .then((res) => setAopSites(Array.isArray(res) ? res : []))
      .catch(() => { });
  }, []);

  useEffect(() => {
    loadCfFormCityOptions()
      .then(setCityOptions)
      .catch(() => setCityOptions([]));
  }, []);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.siteName?.trim()) {
      newErrors.siteName = VALIDATION_ERRORS.siteName;
    }

    if (formData.partOfAOP && !formData.aopStoreId) {
      newErrors.aopStoreId = VALIDATION_ERRORS.aopStoreId;
    }

    if (formData.allDocumentsUploaded) {
      const missingKey = ATTACHMENT_FIELDS.some(({ key }) => !formData.attachments[key]);
      if (missingKey) {
        newErrors.allDocumentsUploaded = VALIDATION_ERRORS.allDocumentsUploaded;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleAddressChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      address: {
        ...prev.address,
        [name]: value,
      },
    }));
  };

  const handleCheckboxChange = (e) => {
    const { checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      partOfAOP: checked,
      aopStoreId: checked ? prev.aopStoreId : "",
    }));
    if (errors.aopStoreId) {
      setErrors((prev) => ({
        ...prev,
        aopStoreId: "",
      }));
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        siteName: formData.siteName,
        address: {
          region: formData.address.region || null,
          circle: formData.address.circle || null,
          city: formData.address.city || null,
        },
        partOfAOP: formData.partOfAOP,
        aopStoreId: formData.partOfAOP
          ? parseInt(formData.aopStoreId, 10)
          : null,
        relocated: formData.relocated,
        allDocumentsUploaded: formData.allDocumentsUploaded,
        attachments: formData.attachments,
      };

      const response = await createCfApprovalV2(payload);
      const cfId = response.id || response._id;
      toast.success(`CF Approval created successfully! ID: ${cfId}`);
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error("Error creating CF Approval:", error);
      toast.error(error?.message || "Failed to create CF Approval");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (onClose) {
      onClose();
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        minWidth: "60%",
        p: onClose ? 0 : 3,
        maxWidth: onClose ? "none" : "900px",
        mx: onClose ? 0 : "auto",
      }}
    >
      {!onClose && <h2>Create New CF Approval (V2)</h2>}

      <Stack spacing={3} sx={{ mt: onClose ? 0 : 2 }}>
        {/* Site Name */}
        <TextField
          label={FORM_LABELS.siteName}
          name="siteName"
          placeholder={FORM_PLACEHOLDERS.siteName}
          value={formData.siteName}
          onChange={handleInputChange}
          fullWidth
          required
          error={!!errors.siteName}
          helperText={errors.siteName}
          size="small"
        />

        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="flex-start"
        >
          {/* Relocated */}
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.relocated}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, relocated: e.target.checked }))
                }
              />
            }
            label={FORM_LABELS.relocated}
          />

          {/* AOP Section */}
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.partOfAOP}
                onChange={handleCheckboxChange}
              />
            }
            label={FORM_LABELS.partOfAOP}
          />

          {/* AOP Store ID - Conditional */}
          {formData.partOfAOP && (
            <Autocomplete
              options={aopSites}
              getOptionLabel={(option) => option.location}
              value={
                aopSites.find((s) => s.srNo === formData.aopStoreId) || null
              }
              onChange={(_, selected) => {
                setFormData((prev) => ({
                  ...prev,
                  aopStoreId: selected ? selected.srNo : "",
                  address: {
                    ...prev.address,
                    region: selected ? selected.region : "",
                    circle: selected ? selected.circle : "",
                    city: selected ? selected.city || "" : "",
                  },
                }));
                if (errors.aopStoreId)
                  setErrors((prev) => ({ ...prev, aopStoreId: "" }));
              }}
              disablePortal
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={FORM_LABELS.aopStoreId}
                  size="small"
                  required
                  sx={{ minWidth: "320px" }}
                  error={!!errors.aopStoreId}
                  helperText={errors.aopStoreId}
                  placeholder="Search by Sr No or Location"
                />
              )}
            />
          )}
        </Stack>

        {/* Address Fields */}
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 2 }}>
          <FormControl fullWidth size="small">
            <FormLabel>{FORM_LABELS.region}</FormLabel>
            <Select
              name="region"
              value={formData.address.region}
              onChange={handleAddressChange}
              displayEmpty
              MenuProps={{ disablePortal: true }}
            >
              <MenuItem value="">
                <em>Select Region</em>
              </MenuItem>
              {REGION_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <FormLabel>{FORM_LABELS.circle}</FormLabel>
            <Select
              name="circle"
              value={formData.address.circle}
              onChange={handleAddressChange}
              displayEmpty
              MenuProps={{ disablePortal: true }}
            >
              <MenuItem value="">
                <em>Select Circle</em>
              </MenuItem>
              {CIRCLE_OPTIONS.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <FormLabel>{FORM_LABELS.city}</FormLabel>
            <Autocomplete
              options={cityOptions}
              value={formData.address.city || null}
              onChange={(_, selected) =>
                setFormData((prev) => ({
                  ...prev,
                  address: { ...prev.address, city: selected || "" },
                }))
              }
              disablePortal
              size="small"
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={FORM_PLACEHOLDERS.city}
                  size="small"
                />
              )}
            />
          </FormControl>
        </Box>
        {/* Upload documents now? */}
        <FormControl component="fieldset">
          <FormLabel>{FORM_LABELS.allDocumentsUploaded}</FormLabel>
          <RadioGroup
            row
            value={formData.allDocumentsUploaded ? "yes" : "no"}
            onChange={(e) => {
              setFormData((prev) => ({
                ...prev,
                allDocumentsUploaded: e.target.value === "yes",
              }));
              if (errors.allDocumentsUploaded) {
                setErrors((prev) => ({ ...prev, allDocumentsUploaded: "" }));
              }
            }}
          >
            <FormControlLabel value="yes" control={<Radio />} label="Yes" />
            <FormControlLabel value="no" control={<Radio />} label="No" />
          </RadioGroup>
          {errors.allDocumentsUploaded && (
            <Typography color="error" variant="caption">
              {errors.allDocumentsUploaded}
            </Typography>
          )}
        </FormControl>

        {/* Attachments */}
        <Box sx={{ border: "1px solid #e0e0e0", p: 2, pb: 4, borderRadius: 1 }}>
          <FormLabel sx={{ mb: 2, display: "block" }}>Attachments</FormLabel>
          <Box
            sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 3 }}
          >
            {ATTACHMENT_FIELDS.map(({ key, label, accept, fileFormats, maxSize }) => (
              <Box key={key}>
                <FormLabel
                  sx={{ display: "block", mb: 0.5, fontSize: "0.85rem" }}
                >
                  {label}
                </FormLabel>
                {formData.attachments[key] ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    noWrap
                    title={formData.attachments[key].split("/").pop()}
                    sx={{ maxWidth: 240, mb: 1 }}
                  >
                    {formData.attachments[key].split("/").pop()}
                  </Typography>
                ) : (
                  <SiFileUpload
                    clsName="relative"
                    inputclassName="absoluteinput"
                    warningclass="cf-approval-warning"
                    documentName={label}
                    url={REST_URLS.UPLOAD_FILES}
                    fileFormats={fileFormats}
                    accept={accept}
                    maxSize={maxSize}
                    showFileName={() => { }}
                    setDataFunction={(url) =>
                      setFormData((prev) => ({
                        ...prev,
                        attachments: { ...prev.attachments, [key]: url },
                      }))
                    }
                  />
                )}
              </Box>
            ))}
          </Box>
        </Box>

        {/* Action Buttons */}
        <Stack
          direction="row"
          spacing={2}
          sx={{ mt: 3, justifyContent: "flex-end" }}
        >
          <Button variant="outlined" onClick={handleCancel} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            startIcon={loading && <CircularProgress size={20} />}
          >
            {loading ? "Creating..." : "Create CF"}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default CreateCfV2Form;
